import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, PhoneCall, MapPin, MessageCircle, FileText, Eye, AlarmClock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_ICONS = {
  llamar: PhoneCall,
  visita: MapPin,
  whatsapp: MessageCircle,
  enviar_presupuesto: FileText,
  seguimiento: Eye,
  recordatorio: AlarmClock,
};

function ActividadItem({ actividad, tone, onComplete, onOpen }) {
  const Icon = TIPO_ICONS[actividad.tipo] || AlarmClock;
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors" data-testid={`notificacion-item-${actividad.id}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <button
        onClick={() => actividad.trial_id && onOpen(actividad.trial_id)}
        className={`flex-1 min-w-0 text-left ${actividad.trial_id ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <p className="text-sm text-white truncate">{actividad.tipo_label}</p>
        {actividad.cliente_nombre && <p className="text-xs text-zinc-400 truncate">{actividad.cliente_nombre}</p>}
        <p className="text-[10px] text-zinc-600">
          {actividad.fecha_vencimiento}{actividad.hora ? ` · ${actividad.hora}` : ''}
        </p>
      </button>
      <button
        onClick={() => onComplete(actividad.id)}
        title="Marcar como completada"
        className="p-1.5 rounded-lg text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
        data-testid={`notificacion-completar-${actividad.id}`}
      >
        <Check className="w-4 h-4" />
      </button>
    </div>
  );
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ vencidas: [], hoy: [], proximas: [], requiere_atencion: 0, total_pendientes: 0 });
  const ref = useRef(null);

  const fetchNotificaciones = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/comercial/notificaciones`, { withCredentials: true });
      setData(response.data);
    } catch (err) {
      // Silencioso: la campana no debe romper la página
    }
  }, []);

  useEffect(() => {
    fetchNotificaciones();
    const interval = setInterval(fetchNotificaciones, 60000);
    return () => clearInterval(interval);
  }, [fetchNotificaciones]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleComplete = async (id) => {
    try {
      await axios.post(`${API_URL}/api/comercial/actividades/${id}/completar`, {}, { withCredentials: true });
      toast.success('Actividad completada');
      fetchNotificaciones();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al completar');
    }
  };

  const handleOpen = (trialId) => {
    setOpen(false);
    navigate(`/comercial/cliente/${trialId}`);
  };

  const badge = data.requiere_atencion;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) fetchNotificaciones(); }}
        className="relative p-2.5 rounded-xl text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
        data-testid="notificaciones-bell"
      >
        <Bell className="w-5 h-5" />
        {badge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notificaciones-badge">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-2xl bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/50 z-50" data-testid="notificaciones-dropdown">
          <div className="p-4 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-white">Notificaciones</h3>
            <p className="text-xs text-zinc-500">{data.total_pendientes} actividades pendientes</p>
          </div>
          <div className="p-2">
            {data.total_pendientes === 0 && (
              <p className="text-sm text-zinc-500 text-center py-8">No tienes tareas pendientes 🎉</p>
            )}
            {data.vencidas.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-red-400 font-medium">Vencidas</p>
                {data.vencidas.map((a) => (
                  <ActividadItem key={a.id} actividad={a} tone="bg-red-500/15 text-red-400" onComplete={handleComplete} onOpen={handleOpen} />
                ))}
              </>
            )}
            {data.hoy.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-amber-400 font-medium">Hoy</p>
                {data.hoy.map((a) => (
                  <ActividadItem key={a.id} actividad={a} tone="bg-amber-500/15 text-amber-400" onComplete={handleComplete} onOpen={handleOpen} />
                ))}
              </>
            )}
            {data.proximas.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Próximas</p>
                {data.proximas.map((a) => (
                  <ActividadItem key={a.id} actividad={a} tone="bg-sky-500/15 text-sky-400" onComplete={handleComplete} onOpen={handleOpen} />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
